/**
 * Battle Royale: the closing firewall and its smoke, the alive count, and the
 * match result. Legacy kept this in Games.js.
 *
 * The server sends GAME_FIREWALL on login, at match start, every five seconds
 * while the match runs, and with status INACTIVE when it ends. Between syncs
 * the ring closes here at the server's speed, in world units per second,
 * negative while it shrinks.
 */
import type { ServerCustomBTRData, ServerGameFirewallMessage } from '@airmash/protocol';
import { BTR_FIREWALL_STATUS } from '@airmash/protocol';
import type { Sprite } from 'pixi.js';
import { Graphics as PixiGraphics } from 'pixi.js';
import { flagFromId } from '../services/Flags';
import { uiState } from '../ui/UIState';
import { clock } from './Clock';
import { MAP_WIDTH } from './Constants';
import { pushGameLogEntry } from './GameLog';
import { getCamera as graphicsGetCamera, gui, minimapMob as graphicsMinimapMob, viewport } from './Graphics';
import { layers } from './Layers';
import { gameComplete as soundGameComplete } from './Sound';
import { sprite as texturesSprite } from './Textures';
import {
  colorLerp as toolsColorLerp,
  distanceSq as toolsDistanceSq,
  rand as toolsRand,
  randInt as toolsRandInt,
} from './Tools';
import { minimapLayout } from './UI';
import Vector from './Vector';

/** The smoke grid: one sprite per cell of this many world units, outside the ring. */
const SMOKE_CELL = 64;

/** The smoke textures, one picked at random per sprite. */
const SMOKE_TEXTURES = ['hotsmoke_1', 'hotsmoke_2', 'hotsmoke_3', 'hotsmoke_4'] as const;

/** A sprite's scale is drawn from this range. */
const SMOKE_SCALE_MIN = 1.5;
const SMOKE_SCALE_MAX = 2.5;

/** The tints a sprite's colour drifts between. */
const SMOKE_TINT_FROM = 0xfaa806;
const SMOKE_TINT_TO = 0xfa4f06;

/** How much a sprite's colour drifts per frame. */
const SMOKE_COLOR_DRIFT = 0.005;

/** The most a sprite spins per frame, either way. */
const SMOKE_SPIN_MAX = 0.0025;

/**
 * How much a sprite fades in per frame: quickly well outside the ring, slowly
 * within a cell of it, where the ring has only just passed.
 */
const SMOKE_FADE_IN_FAR = 0.02;
const SMOKE_FADE_IN_NEAR = 0.0035;

/**
 * The mask circle is drawn at this radius and scaled to the ring. Pixi picks
 * a circle's segments from the radius it is drawn at, four for a unit circle
 * and fifty-three here, more than it gives any size the minimap can show.
 */
const MASK_RADIUS = 256;

/** BTR firewall sprite animation data */
interface FirewallSpriteData {
  sprite: Sprite;
  rotation: number;
  rotationSpeed: number;
  opacity: number;
  maxOpacity: number;
  opacitySpeed: number;
  color: number;
  colorDir: number;
}

/** Active BTR firewall smoke sprites keyed by packed grid coordinate */
const firewallSprites = new Map<number, FirewallSpriteData>();

/** Pre-allocated set for tracking active firewall sprite positions per frame */
const activeFirewallSprites = new Set<number>();

/**
 * The minimap's firewall mask: one circle moved to the ring's centre and
 * scaled to its radius each frame, so its geometry is built once. Non-null
 * exactly while the firewall is shown.
 */
let minimapFirewallMask: PixiGraphics | null = null;

const firewall = {
  radius: 0,
  pos: Vector.zero(),
  speed: 0,
};

/** Track previous players alive count to avoid duplicate logs */
let prevPlayersAlive = 0;

/** Clears the BTR state and removes the firewall. Safe to call in any game mode. */
export function wipe(): void {
  uiState.btrPlayersAlive = null;
  prevPlayersAlive = 0;
  removeFirewall();
}

/** Logs the alive count when it changes, while more than one player is left; the feed applies its own setting. */
function addPlayersAliveToGameLog(playersAlive: number) {
  // Only log if count changed and is meaningful (> 1 player)
  if (playersAlive === prevPlayersAlive || playersAlive <= 1) {
    prevPlayersAlive = playersAlive;
    return;
  }
  prevPlayersAlive = playersAlive;

  pushGameLogEntry({ type: 'players-alive', playersAlive });
}

/** Handles GAME_PLAYERSALIVE message */
export function playersAlive(numberAlive: number): void {
  // Log to game log
  addPlayersAliveToGameLog(numberAlive);

  // Update HUD state for the Lit component
  uiState.btrPlayersAlive = numberAlive;
}

/** Handles SERVER_CUSTOM type 1 message for BTR game win */
export function showWin(msg: ServerCustomBTRData): void {
  const flag = flagFromId(msg.f);

  // Log to game log
  pushGameLogEntry({ type: 'btr-win', playerName: msg.p, flag, kills: msg.k, bounty: msg.b });

  // Only display if not already showing a BTR result
  if (!uiState.btrMatchResult) {
    uiState.btrMatchResult = {
      playerName: msg.p,
      flag,
      kills: msg.k,
      bounty: msg.b,
      duration: msg.t,
    };
    soundGameComplete();
  }
}

/**
 * Handles GAME_FIREWALL: the ring's centre, radius and speed as of now, or
 * its removal at the end of a match. The type field is unused by every client.
 */
export function handleFirewall(msg: ServerGameFirewallMessage): void {
  if (msg.status === BTR_FIREWALL_STATUS.INACTIVE) {
    removeFirewall();
  } else {
    firewall.radius = msg.radius;
    firewall.pos.x = msg.posX;
    firewall.pos.y = msg.posY;
    firewall.speed = msg.speed;
    popFirewall(firewall.pos, firewall.radius);
  }
}

/** Frame update: the ring closes at the server's speed and the smoke follows the camera. */
export function update(isResize: boolean): void {
  if (minimapFirewallMask === null) return;
  // A resize only redraws the mask at the new minimap size; advancing the
  // ring there too made it run ahead of the server during zoom drags.
  if (!isResize) {
    // Speed is per second: a sixtieth per frame at time factor 1
    firewall.radius += (firewall.speed / 60) * clock.timeFactor;
  }
  popFirewall(firewall.pos, firewall.radius);
}

/** Updates BTR firewall sprites and minimap mask */
function popFirewall(firewallPosition: Vector, firewallRadius: number) {
  const radius = firewallRadius <= 0 ? 0 : firewallRadius;

  // Create the minimap mask the first time the firewall shows
  let mask = minimapFirewallMask;
  if (mask === null) {
    const minimap = gui.minimap;
    if (minimap === undefined) return; // exists once Graphics.setup has run
    mask = new PixiGraphics().circle(0, 0, MASK_RADIUS).fill({ color: 0xffffff });
    minimap.parent?.addChild(mask);
    minimap.mask = mask;
    minimapFirewallMask = mask;
  }

  // The ring on the minimap: the dots' mapping places the centre, and the
  // map-to-minimap scale sizes the radius
  graphicsMinimapMob(mask, firewallPosition.x, firewallPosition.y);
  mask.scale.set((radius * minimapLayout.size * minimapLayout.scale) / MAP_WIDTH / MASK_RADIUS);

  // Calculate extent of firewall sprite grid
  const horizontalSpriteCount = Math.ceil((viewport.halfWidth + SMOKE_CELL) / viewport.scale / SMOKE_CELL);
  const verticalSpriteCount = Math.ceil((viewport.halfHeight + SMOKE_CELL) / viewport.scale / SMOKE_CELL);
  activeFirewallSprites.clear();

  // Corner coordinates of the visible area (scalars, no Vector allocation)
  const camera = graphicsGetCamera();
  const leftX = camera.x - viewport.halfWidth / viewport.scale - SMOKE_CELL;
  const rightX = camera.x + viewport.halfWidth / viewport.scale + SMOKE_CELL;
  const topY = camera.y - viewport.halfHeight / viewport.scale - SMOKE_CELL;
  const bottomY = camera.y + viewport.halfHeight / viewport.scale + SMOKE_CELL;
  const radiusSq = radius * radius;

  // Any corner of the view outside the ring means there is smoke to show
  if (
    toolsDistanceSq(firewallPosition.x, firewallPosition.y, leftX, topY) > radiusSq ||
    toolsDistanceSq(firewallPosition.x, firewallPosition.y, rightX, topY) > radiusSq ||
    toolsDistanceSq(firewallPosition.x, firewallPosition.y, leftX, bottomY) > radiusSq ||
    toolsDistanceSq(firewallPosition.x, firewallPosition.y, rightX, bottomY) > radiusSq
  ) {
    // Iterate over firewall sprite grid
    const baseGridX = Math.floor(camera.x / SMOKE_CELL);
    const baseGridY = Math.floor(camera.y / SMOKE_CELL);
    for (let x = -horizontalSpriteCount; x <= horizontalSpriteCount; x++) {
      const gridX = baseGridX + x;
      const posX = SMOKE_CELL * (gridX + 0.5);
      for (let y = -verticalSpriteCount; y <= verticalSpriteCount; y++) {
        const gridY = baseGridY + y;
        const posY = SMOKE_CELL * (gridY + 0.5);
        const distanceSq = toolsDistanceSq(posX, posY, firewallPosition.x, firewallPosition.y);

        // A cell on or outside the ring shows hot smoke
        if (distanceSq >= radiusSq) {
          const key = ((gridX & 0xffff) << 16) | (gridY & 0xffff);
          activeFirewallSprites.add(key);

          // Create firewall sprite for this position if it doesn't already exist
          if (!firewallSprites.has(key)) {
            const texture = SMOKE_TEXTURES[toolsRandInt(0, SMOKE_TEXTURES.length - 1)] ?? SMOKE_TEXTURES[0];
            const firewallSprite = texturesSprite(texture);

            firewallSprite.scale.set(toolsRand(SMOKE_SCALE_MIN, SMOKE_SCALE_MAX));
            firewallSprite.anchor.set(0.5, 0.5);
            firewallSprite.position.set(posX, posY);
            // Half the sprites are translucent and additive, so the smoke reads as layered
            const maxOpacity = toolsRand(0, 1) > 0.5 ? 0.5 : 1;
            if (maxOpacity === 0.5) {
              firewallSprite.blendMode = 'add';
            }

            layers.powerups.addChild(firewallSprite);

            // Within a cell of the ring the smoke fades in slowly, as the ring has only just passed
            const nearRing = Math.sqrt(distanceSq) - radius < SMOKE_CELL;
            firewallSprites.set(key, {
              sprite: firewallSprite,
              rotation: toolsRand(0, 100),
              rotationSpeed: toolsRand(-SMOKE_SPIN_MAX, SMOKE_SPIN_MAX),
              opacity: 0,
              maxOpacity,
              opacitySpeed: nearRing ? SMOKE_FADE_IN_NEAR : SMOKE_FADE_IN_FAR,
              color: toolsRand(0, 1),
              colorDir: toolsRand(0, 1) < 0.5 ? -1 : 1,
            });
          }
        }
      }
    }
  }

  for (const [key, spriteData] of firewallSprites) {
    if (activeFirewallSprites.has(key)) {
      // Animate sprite by varying its display properties
      spriteData.rotation += spriteData.rotationSpeed * clock.timeFactor;
      spriteData.opacity += spriteData.opacitySpeed * clock.timeFactor;
      if (spriteData.opacity > spriteData.maxOpacity) {
        spriteData.opacity = spriteData.maxOpacity;
      }
      spriteData.color += SMOKE_COLOR_DRIFT * spriteData.colorDir * clock.timeFactor;
      if (spriteData.color < 0) {
        spriteData.colorDir = 1;
      }
      if (spriteData.color > 1) {
        spriteData.colorDir = -1;
      }

      // Apply properties to sprite
      spriteData.sprite.rotation = spriteData.rotation;
      spriteData.sprite.alpha = spriteData.opacity;
      spriteData.sprite.tint = toolsColorLerp(SMOKE_TINT_FROM, SMOKE_TINT_TO, spriteData.color);
    } else {
      // Remove this firewall sprite; destroy takes it off its layer
      spriteData.sprite.destroy();
      firewallSprites.delete(key);
    }
  }
}

/** Removes all BTR firewall sprites and the minimap mask */
function removeFirewall() {
  if (minimapFirewallMask === null) return;

  for (const spriteData of firewallSprites.values()) {
    spriteData.sprite.destroy();
  }
  firewallSprites.clear();

  const minimap = gui.minimap;
  if (minimap !== undefined) {
    minimap.mask = null;
  }
  minimapFirewallMask.destroy();
  minimapFirewallMask = null;
}
